import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, PaperPlaneTilt, CheckCircle, Envelope, ArrowLeft, ArrowRight,
  Buildings, MapTrifold, Coins, Rocket,
} from "@phosphor-icons/react";
import api from "@/lib/api";

import BuyerCombobox from "@/components/shared/BuyerCombobox";
import StateCheckboxSelector, {
  US_STATES,
} from "@/components/shared/StateCheckboxSelector";

/**
 * Iter-75 — Quick-setup buyer wizard.
 *
 * A 4-step, non-technical-friendly flow for creating an EMAIL delivery
 * destination attached to a buyer, without exposing the full
 * `DestinationForm` (12 destination types, CRM/OAuth/S3/FTP fields,
 * blocked-affiliates, campaign filters, etc.).
 *
 * Steps:
 *   1. Pick an existing buyer OR create a new one inline.
 *   2. Recipient email (with an implicit test on step 4).
 *   3. Accepted states + a single flat $/lead price (fanned out to
 *      per-state fees on save).
 *   4. Return-window hours + buyer price (Iter-74.2 fields) — both
 *      OPTIONAL with sensible defaults (72 h return, price shown but
 *      left blank if the user skips).
 *   5. Save + fire one synthetic test lead so the buyer confirms
 *      receipt before real traffic starts flowing.
 *
 * Reuses existing endpoints — zero backend changes:
 *   POST /api/organizations            (type=client)  — create new buyer
 *   POST /api/delivery/destinations                    — create dest
 *   POST /api/delivery/destinations/:id/test           — fire test lead
 *
 * `onCreated(destination)` fires as soon as the destination row is
 * saved (before the test lead) so the parent `/delivery` grid can
 * refresh in the background.
 */
const STEPS = [
  { key: "buyer",   label: "Buyer",   Icon: Buildings },
  { key: "email",   label: "Email",   Icon: Envelope },
  { key: "states",  label: "States",  Icon: MapTrifold },
  { key: "money",   label: "Pricing", Icon: Coins },
];

export default function QuickSetupBuyerModal({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saved, setSaved] = useState(null);          // created destination
  const [testResult, setTestResult] = useState(null);
  // Iter-75.1 — cache the buyer that the inline "+ create new buyer"
  // branch spawns, so a Save-then-fail-then-retry does NOT create a
  // second identical buyer org. Cleared when the wizard is reset via
  // "Set up another buyer".
  const [inlineBuyerId, setInlineBuyerId] = useState(null);
  const [inlineBuyerName, setInlineBuyerName] = useState(null);

  // Step 1 — Buyer
  const [buyer, setBuyer] = useState(null);          // {id, name}
  const [creatingBuyer, setCreatingBuyer] = useState(false);
  const [newBuyer, setNewBuyer] = useState({
    name: "",
    contact_email: "",
    contact_phone: "",
  });

  // Step 2 — Email
  const [toEmail, setToEmail] = useState("");

  // Step 3 — States + flat price
  const [states, setStates] = useState([]);          // string[] of codes
  const [flatPrice, setFlatPrice] = useState("");    // string in dollars

  // Step 4 — Return window + buyer price
  const [returnHours, setReturnHours] = useState("72");
  const [buyerPrice, setBuyerPrice] = useState("");

  /** True when the current step's minimum inputs are filled. */
  const canAdvance = useMemo(() => {
    if (step === 0) {
      if (creatingBuyer) return newBuyer.name.trim().length > 0;
      return !!buyer?.id;
    }
    if (step === 1) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim());
    if (step === 2) return states.length > 0 && Number(flatPrice) > 0;
    if (step === 3) return Number(returnHours) >= 0;
    return true;
  }, [step, buyer, creatingBuyer, newBuyer, toEmail, states, flatPrice, returnHours]);

  const goBack  = () => setStep((s) => Math.max(0, s - 1));
  const goNext  = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));

  const resetToStart = () => {
    setStep(0);
    setSaved(null);
    setTestResult(null);
    setInlineBuyerId(null);
    setInlineBuyerName(null);
  };

  /**
   * On Save: idempotent create-buyer-if-needed, then create the email
   * destination with the flat price fanned out into per-state fees.
   */
  const submit = async () => {
    setSaving(true);
    try {
      let buyerId = buyer?.id;
      let buyerName = buyer?.name;
      if (creatingBuyer) {
        // Iter-75.1 — reuse the cached buyer if we already POSTed one
        // on a previous Save attempt that failed at the destination step.
        // Prevents orphan buyer orgs from piling up on retry.
        if (inlineBuyerId) {
          buyerId = inlineBuyerId;
          buyerName = inlineBuyerName;
        } else {
          const r = await api.post("/organizations", {
            type: "client",
            name: newBuyer.name.trim(),
            contact_email: newBuyer.contact_email.trim() || null,
            contact_phone: newBuyer.contact_phone.trim() || null,
            status: "active",
          });
          buyerId = r.data.id;
          buyerName = r.data.name;
          setInlineBuyerId(buyerId);
          setInlineBuyerName(buyerName);
          toast.success(`Buyer created — ${r.data.name}`);
        }
      }
      const flat = Number(flatPrice);
      const stateFees = states.reduce((acc, code) => {
        acc[code] = flat;
        return acc;
      }, {});
      const payload = {
        client_id: buyerId,
        name: `${buyerName || "Buyer"} — email intake`,
        type: "email",
        is_active: true,
        retry_policy: { max_attempts: 5 },
        config: {
          to: toEmail.trim(),
          state_allowlist: states,
          state_fees: stateFees,
          payment_mode: "prepay",
          template: "",
        },
        buyer_price: buyerPrice ? Number(buyerPrice) : null,
        return_window_hours: returnHours ? Number(returnHours) : null,
      };
      const r = await api.post("/delivery/destinations", payload);
      setSaved(r.data);
      onCreated?.(r.data);
      toast.success(`Destination created — ${r.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!saved) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`/delivery/destinations/${saved.id}/test`);
      setTestResult({ ok: true, ...r.data });
      toast.success("Test lead dispatched — ask the buyer to check the inbox");
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.detail || String(err) });
      toast.error("Test failed — see details");
    } finally {
      setTesting(false);
    }
  };

  /** Auto-close on hard cancel. */
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saved && !saving && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saved, saving, onClose]);

  const Body = (
    <>
      {!saved && (
        <div className="border-b border-black px-6 py-3 flex items-center gap-2 flex-wrap" data-testid="quick-setup-steps">
          {STEPS.map((s, i) => {
            const Icon = s.Icon;
            const active = i === step;
            const done = i < step;
            return (
              <div
                key={s.key}
                data-testid={`quick-setup-step-${s.key}`}
                className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border ${active ? "bg-black text-white border-black" : done ? "border-emerald-700 text-emerald-700 bg-emerald-50" : "border-zinc-300 text-zinc-500"}`}
              >
                <Icon size={11} weight="bold" />
                {i + 1}. {s.label}
              </div>
            );
          })}
        </div>
      )}

      {!saved && (
        <div className="p-6 space-y-4">
          {step === 0 && (
            <div className="space-y-3" data-testid="quick-setup-pane-buyer">
              <div className="text-sm text-zinc-700">
                Who is buying these leads? Pick an existing law firm buyer, or
                create a fresh one right here (no need to leave this dialog).
              </div>
              {!creatingBuyer && (
                <>
                  <BuyerCombobox
                    value={buyer}
                    onChange={setBuyer}
                    placeholder="Search buyers by name…"
                    testIdPrefix="quick-setup-buyer"
                    includeAll={false}
                  />
                  <button
                    type="button"
                    data-testid="quick-setup-create-buyer-toggle"
                    className="text-xs underline text-zinc-600 hover:text-black"
                    onClick={() => { setCreatingBuyer(true); setBuyer(null); }}
                  >
                    + Buyer not in the list — create it now
                  </button>
                </>
              )}
              {creatingBuyer && (
                <div className="border-2 border-black p-3 space-y-2 bg-amber-50">
                  <div className="label-mono">Create new buyer</div>
                  <input
                    className="w-full border border-black px-3 py-2"
                    placeholder="Firm name (required)"
                    data-testid="quick-setup-newbuyer-name"
                    value={newBuyer.name}
                    onChange={(e) => setNewBuyer((f) => ({ ...f, name: e.target.value }))}
                  />
                  <input
                    className="w-full border border-black px-3 py-2"
                    placeholder="Contact email (optional)"
                    data-testid="quick-setup-newbuyer-email"
                    value={newBuyer.contact_email}
                    onChange={(e) => setNewBuyer((f) => ({ ...f, contact_email: e.target.value }))}
                  />
                  <input
                    className="w-full border border-black px-3 py-2"
                    placeholder="Contact phone (optional)"
                    data-testid="quick-setup-newbuyer-phone"
                    value={newBuyer.contact_phone}
                    onChange={(e) => setNewBuyer((f) => ({ ...f, contact_phone: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="text-xs underline text-zinc-600 hover:text-black"
                    onClick={() => setCreatingBuyer(false)}
                    data-testid="quick-setup-create-buyer-cancel"
                  >
                    ← Back to picking an existing buyer
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="quick-setup-pane-email">
              <div className="text-sm text-zinc-700">
                Where should each qualified lead be emailed? A test lead
                will be sent to this address at the end so the buyer can
                confirm it arrived before real traffic starts.
              </div>
              <div>
                <label className="label-mono">Recipient email</label>
                <input
                  className="w-full border border-black px-3 py-2 mt-1"
                  type="email"
                  placeholder="intake@yourfirm.com"
                  data-testid="quick-setup-to"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                />
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                Emails include first name, last name, phone, email, state,
                zip, case summary, and any collected photos. Failed sends
                automatically retry (30 s → 6 h ladder, 5 tries). Replies go to your support inbox.
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="quick-setup-pane-states">
              <div className="text-sm text-zinc-700">
                Which US states will this buyer accept, and what do they pay
                per lead? The same flat price is applied to every state you
                pick — you can fine-tune per-state fees later on the full
                Delivery Destinations page.
              </div>
              <StateCheckboxSelector
                value={states}
                onChange={setStates}
                testId="quick-setup-states"
                helpText="Tip: use the “Continental only” shortcut if the firm doesn't handle AK / HI cases."
              />
              <div>
                <label className="label-mono">Flat price per lead (USD)</label>
                <input
                  className="w-full border border-black px-3 py-2 mt-1"
                  type="number" min="0" step="0.01"
                  placeholder="e.g. 350"
                  data-testid="quick-setup-flat-price"
                  value={flatPrice}
                  onChange={(e) => setFlatPrice(e.target.value)}
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Applied to every state you selected above (
                  {states.length ? `${states.length} state${states.length === 1 ? "" : "s"}` : "0 states"}
                  ). A positive amount is required — the readiness check
                  rejects $0 / blank price.
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="quick-setup-pane-money">
              <div className="text-sm text-zinc-700">
                Two last details for the buyer contract. Both are optional —
                sensible defaults will apply if you skip them.
              </div>
              <div>
                <label className="label-mono">Return window (hours)</label>
                <input
                  className="w-full border border-black px-3 py-2 mt-1"
                  type="number" min="0" step="1"
                  placeholder="72 = three days"
                  data-testid="quick-setup-return-window"
                  value={returnHours}
                  onChange={(e) => setReturnHours(e.target.value)}
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  How long the buyer has to dispute a lead after delivery.
                  72 h (three business days) is a common default.
                </div>
              </div>
              <div>
                <label className="label-mono">Buyer price per accepted lead (USD, optional)</label>
                <input
                  className="w-full border border-black px-3 py-2 mt-1"
                  type="number" min="0" step="0.01"
                  placeholder="Leave blank to skip — invoices will still send using the per-state fees above"
                  data-testid="quick-setup-buyer-price"
                  value={buyerPrice}
                  onChange={(e) => setBuyerPrice(e.target.value)}
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  A separate top-line price (used by automatic invoicing).
                  Leaving it blank keeps the per-state fees as the source of
                  truth for billing.
                </div>
              </div>
              <div className="border-t-2 border-black pt-3 mt-3 text-xs">
                <div className="font-bold uppercase tracking-widest text-[10px] mb-1">
                  Review
                </div>
                <ul className="space-y-1 text-zinc-700">
                  <li>Buyer: <b>{creatingBuyer ? newBuyer.name : buyer?.name || "—"}</b></li>
                  <li>Email: <b>{toEmail || "—"}</b></li>
                  <li>States: <b>{states.length ? states.join(", ") : "—"}</b></li>
                  <li>Price / lead: <b>{flatPrice ? `$${Number(flatPrice).toFixed(2)}` : "—"}</b></li>
                  <li>Return window: <b>{returnHours || "—"} h</b></li>
                  <li>Buyer price (optional): <b>{buyerPrice ? `$${Number(buyerPrice).toFixed(2)}` : "—"}</b></li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {saved && (
        <div className="p-6 space-y-5" data-testid="quick-setup-done">
          <div className="border-2 border-emerald-700 bg-emerald-50 p-4 flex items-start gap-3">
            <CheckCircle size={20} weight="fill" className="text-emerald-700 mt-0.5" />
            <div className="flex-1">
              <div className="font-chivo font-black uppercase tracking-tighter text-base">
                {saved.name} — saved
              </div>
              <div className="text-xs text-zinc-700 mt-1">
                The destination is live. Fire a test lead now so the buyer
                confirms delivery before real traffic starts.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={sendTest}
              disabled={testing}
              data-testid="quick-setup-send-test"
              className="border-2 border-black bg-black text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-2"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {testing ? "Sending…" : "Send test lead"}
            </button>
            <button
              type="button"
              onClick={resetToStart}
              data-testid="quick-setup-add-another"
              className="border border-black px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Set up another buyer
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="quick-setup-done-close"
              className="ml-auto border border-black px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Done
            </button>
          </div>
          {testResult && (
            <div
              data-testid="quick-setup-test-result"
              className={`border-2 p-3 text-xs ${testResult.ok ? "border-emerald-700 bg-emerald-50" : "border-red-700 bg-red-50"}`}
            >
              <div className="font-bold uppercase tracking-widest text-[10px] mb-1">
                {testResult.ok ? "Delivered" : "Failed"}
              </div>
              <pre className="whitespace-pre-wrap break-all font-mono text-[10px] leading-relaxed">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {!saved && (
        <div className="border-t-2 border-black px-6 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0 || saving}
            data-testid="quick-setup-back"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-30 inline-flex items-center gap-1"
          >
            <ArrowLeft size={11} weight="bold" /> Back
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="quick-setup-cancel"
              className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Cancel
            </button>
            {step < STEPS.length - 1 && (
              <button
                type="button"
                onClick={goNext}
                disabled={!canAdvance || saving}
                data-testid="quick-setup-next"
                className="border-2 border-black bg-black text-white px-3 py-2 text-xs uppercase tracking-widest font-bold disabled:opacity-30 hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-1"
              >
                Next <ArrowRight size={11} weight="bold" />
              </button>
            )}
            {step === STEPS.length - 1 && (
              <button
                type="button"
                onClick={submit}
                disabled={!canAdvance || saving}
                data-testid="quick-setup-save"
                className="border-2 border-black bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-widest font-bold disabled:opacity-30 hover:bg-emerald-800 inline-flex items-center gap-1"
              >
                <Rocket size={11} weight="bold" />
                {saving ? "Saving…" : "Create destination"}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="quick-setup-modal"
    >
      <div className="bg-white border-4 border-black max-w-2xl w-full shadow-[8px_8px_0_0_#059669]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <Rocket size={16} weight="bold" className="text-emerald-700" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            Quick setup — email buyer
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-setup-close"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
        {Body}
      </div>
    </div>
  );
}
